import { ofType } from 'redux-observable';
import { from, of, Observable } from 'rxjs';
import {
  catchError,
  switchMap,
  tap,
  filter,
  ignoreElements,
  map,
} from 'rxjs/operators';

import { ActionStatus, TokenExchangeResultCode } from '@parapet/common';
import { State } from '../root';
import { EpicDependencies } from '../epic-middleware';
import { AUTH_TICKET_URL } from '../urls';
import * as actions from './actions';
import * as types from './types';
import {
  AuthTicketResponse,
  CompanyAuthTicketResponse,
} from './models/auto-ticket-response.interface';
import { UserCredentialsDto } from './models/user-credentials-dto.type';

/**
 * Handles server communication for auth using login form.
 */
export const authUser$ = (
  actions$: Observable<types.AuthUser>,
  state$: Observable<State>,
  { http, cookie }: EpicDependencies,
) =>
  actions$.pipe(
    ofType(types.AUTH_USER),
    switchMap((action) => {
      const isCompanyAuth = action.company && action.callback;

      if (isCompanyAuth) {
        const { email, password, company, callback } = action;
        return [
          actions.authCompanyUser({
            email,
            password,
            company: company!,
            callback: callback!,
          }),
        ];
      }

      const params: UserCredentialsDto =
        action.authType === 'email'
          ? {
              email: action.email,
              password: action.password,
            }
          : { phoneNumber: action.phone, password: action.password };

      const authRequest = http
        .post<Response>({
          url: AUTH_TICKET_URL,
          data: params,
          rawResponse: true,
        })
        .asObservable();

      return authRequest.pipe(
        switchMap((res) => (res.status === 200 ? res.json() : res.text())),
        switchMap((res) => {
          if (typeof res === 'object') {
            // Response is json
            return of(res as AuthTicketResponse);
          } else if (+res === TokenExchangeResultCode.LockedOut) {
            window.location.replace('/forgot-password?locked-out');
          } else if (+res !== TokenExchangeResultCode.Success) {
            throw new Error(res);
          }
          return [];
        }),
        switchMap(({ ticketUrl }) => {
          // Write temp ticket token to cookie
          const url = ticketUrl ?? '';
          const token = url.split('?token=').pop()!;
          // Dev environment requires a cookie since DOM token will only be served in production.
          const cookieData = {
            token,
            email: action.email,
            phone: action.phone,
            authType: action.authType,
          };
          cookie.set(JSON.stringify(cookieData));
          return [
            actions.setAuthStatus(ActionStatus.success),
            actions.setAuthToken(token),
          ];
        }),
        catchError(() => [actions.authUserFailure()]),
      );
    }),
  );

/**
 * Redirect to shell once auth token has been set.
 */
export const redirect$ = (actions$: Observable<types.SetAuthToken>) =>
  actions$.pipe(
    ofType(types.SET_AUTH_TOKEN),
    map(({ token }) => token),
    filter((token) => !!token),
    tap((token) => {
      const url = `${
        process.env.REACT_APP_ENV !== 'local'
          ? process.env.REACT_APP_SHELL_URL!
          : 'http://localhost:3000'
      }?token=${token}`;

      // console.log('auth : token set, going to shell :', url);
      window.location.assign(url);
    }),
    ignoreElements(),
  );

/**
 * Redirects to callback when params are present.
 * @example:
 * http://localhost:4000/login?company=133&callback=4%2Fapi%2FShared%3Fpublicidentifier=f4ddb253-3f5b-4fe0-8ec0-8a527dfbe9d9
 */
export const authCompanyUser$ = (
  actions$: Observable<types.AuthCompanyUser>,
  state$: Observable<State>,
  { http, cookie, isDev }: EpicDependencies,
) =>
  actions$.pipe(
    ofType(types.AUTH_COMPANY_USER),
    switchMap((action) => {
      const url = `${AUTH_TICKET_URL}/company/${action.company}`;
      const params = {
        email: action.email,
        password: action.password,
        phone: action.phone,
      };

      const req = http
        .post<Response>({ url, data: params, rawResponse: true })
        .asObservable();

      return from(req).pipe(
        switchMap((res) => (res.status === 200 ? res.json() : res.text())),
        switchMap((res) => {
          if (typeof res === 'object') {
            // Response is json
            return of(res as CompanyAuthTicketResponse);
          } else if (+res === TokenExchangeResultCode.LockedOut) {
            window.location.replace('/forgot-password?locked-out');
          } else if (+res !== TokenExchangeResultCode.Success) {
            throw new Error(res);
          }
          return [];
        }),
        switchMap(({ ticket }) => {
          // Write temp ticket token to cookie
          if (isDev) {
            // Dev environment requires a cookie since DOM token will only be served in production.
            const cookieData = {
              token: ticket,
              email: action.email,
              phone: action.phone,
              authType: action.authType,
            };
            cookie.set(JSON.stringify(cookieData));
          }

          const productURLMap = {
            '1': 'https://insight.parapetstudios.com',
            '2': 'https://overseer.parapetstudios.com',
            '3': 'https://users.parapetstudios.com',
            '4': 'https://check.parapetstudios.com',
          };
          const productId = action.callback[0] as keyof typeof productURLMap;
          const productURL = productURLMap[productId];
          const callback = action.callback.slice(1);
          const url = `${productURL}${callback}&ticket=${ticket}`;
          return [
            actions.setAuthStatus(ActionStatus.success),
            actions.authCompanyUserSuccess(url),
          ];
        }),
        catchError(() => [actions.authUserFailure()]),
      );
    }),
  );

/**
 * Opens resource for download in new tab.
 */
export const openRedirectURL$ = (
  actions$: Observable<types.AuthCompanyUserSuccess>,
) =>
  actions$.pipe(
    ofType(types.AUTH_COMPANY_USER_SUCCESS),
    tap(({ url }) => window.open(url, '_blank')),
    filter(() => false),
  );

export const userEpics = [
  authUser$,
  authCompanyUser$,
  redirect$,
  openRedirectURL$,
];
